import React from "react";
import { Popover, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Paragraph, Text } = Typography;

/** A table column header with an info icon that explains the column in plain language. */
const HeaderHelp: React.FC<{ label: string; help: React.ReactNode }> = ({ label, help }) => (
  <span style={{ whiteSpace: "nowrap" }}>
    {label}{" "}
    <Tooltip title={help} overlayStyle={{ maxWidth: 360 }}>
      <InfoCircleOutlined style={{ color: "#1677ff", cursor: "help" }} />
    </Tooltip>
  </span>
);

export default HeaderHelp;

/** Plain-language meanings of the airing columns in the Scores drawer. */
export const AIRING_COLUMN_HELP = {
  slotVsAverage:
    "How strong this time slot (weekday and hour, on this platform and channel) is compared with an average slot. " +
    "+20% means the slot normally draws 20% more than average, -15% means less. Built from every series that aired there, " +
    "after allowing for how strong each series is, so it measures the hour, not the shows.",
  expectedRate:
    "Hours of viewing (HOV) per minute of airtime that an average episode of this series would earn in this slot. " +
    "It is proportional to the average audience watching at any moment. Think of it as what the slot and the series " +
    "alone would deliver, before this particular episode comes into it.",
  actualRate:
    "What this airing actually earned: its hours of viewing (minutes watched divided by 60) divided by its length " +
    "in minutes. Same unit as the expected rate, so the two compare directly.",
  airingVsSlot:
    "Actual rate against expected rate, as a percentage. +10% means this airing drew 10% more than an average episode " +
    "of the series would have in the same slot. A V1 score is the plain average of this column over the episode's " +
    "airings; an M4 score also removes day, lead-in, fatigue and ad-load effects and shrinks toward the series, so it " +
    "will not equal that average.",
  mergedPieces:
    "Amagi sometimes reports one interrupted playout as several rows a few seconds apart. This is how many such rows " +
    "were stitched into this airing. Blank means it arrived as a single row.",
};

/** Plain-language meanings of the columns in the main Scores table. */
export const SCORE_COLUMN_HELP = {
  score:
    "How much better or worse this episode does than an average episode of its series, after taking out the effect " +
    "of when and where it aired. +5% means it drew 5% more audience than an average episode would have earned in the " +
    "same slots. It is the average over the episode's airings, pulled toward 0 when there are only a few of them.",
  se:
    "The margin of error on the score, in the same percent units. +4.0 ±0.7 means the episode's true effect is most " +
    "likely between +3.3 and +4.7. It shrinks as airings accumulate. M4 only; V1 rows leave it blank. Click the score in " +
    "the drawer for the longer explanation.",
  bucket:
    "A coarse grade from the score: low is the bottom third of all episodes, average the middle third, high the top " +
    "third of the same model's episodes. The data tells the two ends apart well and the middle poorly, so the bucket is " +
    "the safer thing to act on than the exact rank.",
  confidence:
    "Strong when the score rests on at least 6 airings, thin when it rests on fewer. A thin score can move a lot once " +
    "the next few airings arrive, so treat it as provisional.",
  source:
    "Own: computed from this feed's or platform's own airings. Borrowed: that platform's data was too noisy to score " +
    "on its own, so the row shows the pooled score from the reliable platforms on the same channel instead.",
};

/** An info icon that opens a longer explanation on click, for controls rather than column headers. */
export const HelpPopover: React.FC<{ title: string; content: React.ReactNode }> = ({ title, content }) => (
  <Popover trigger="click" placement="bottomLeft" overlayStyle={{ maxWidth: 560 }} title={title} content={content}>
    <InfoCircleOutlined style={{ color: "#1677ff", cursor: "pointer", fontSize: 16 }} />
  </Popover>
);

export const ScopeHelp: React.FC = () => (
  <HelpPopover
    title="How the three score levels relate"
    content={
      <>
        <Paragraph>
          Every airing is scored against the slot it landed in, on its own platform. From there the scores are
          built up in tiers, and each level is a view of the same episodes:
        </Paragraph>
        <Paragraph>
          <Text strong>Per platform.</Text> The episode&apos;s airings on one platform and channel, averaged. Shown as{" "}
          <Text italic>own</Text> when that platform&apos;s data is reliable enough to stand alone, or <Text italic>borrowed</Text>{" "}
          when it is too noisy, in which case the row carries the pooled score from the reliable platforms of the same channel.
        </Paragraph>
        <Paragraph>
          <Text strong>Per feed.</Text> One schedule goes out to many platforms at once (Nosey INTL 1 reaches about twenty),
          so the scheduler needs one number per feed. It is the reliable platforms&apos; scores blended together, each weighted
          by how many minutes that platform watched, so the big audiences count most.
        </Paragraph>
        <Paragraph>
          <Text strong>Overall.</Text> The episode across every channel and platform, blended by how trustworthy each
          platform&apos;s score is (its airings times its reliability) rather than by audience size. This is the best single
          estimate of how good the episode is.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>M4 rows differ in one way.</Text> M4 has no per-platform level: it pools the platforms that carried
          the same airing into one observation before scoring, so its <Text italic>per feed</Text> row is already the whole
          channel, and its <Text italic>overall</Text> row combines channels by how precisely each measured the episode.
          The episode record carries the overall score of the model marked as canonical (currently M4).
        </Paragraph>
      </>
    }
  />
);

export const ModelHelp: React.FC = () => (
  <HelpPopover
    title="Two models, same airings"
    content={
      <>
        <Paragraph>
          Every run scores every episode with both models from the same cleaned airings, and this filter picks which
          one the table shows. Each model&apos;s rows are bucketed and ranked among themselves, so switching the filter
          switches the whole view, not just one column.
        </Paragraph>
        <Paragraph>
          <Text strong>V1</Text> is the original engine. For each platform and channel it works out how strong every
          time slot and every series is, scores each airing against them, averages the episode&apos;s airings, pulls thin
          episodes toward zero, and blends the reliable platforms into one feed score.
        </Paragraph>
        <Paragraph>
          <Text strong>M4</Text> is the October 2026 model. On top of slot and series it also removes the effect of the
          particular day, of the audience handed over by the previous show (lead-in), of how recently the episode last
          aired (fatigue) and of the ad load. It then pools the platforms that carried the same airing into one
          observation and shrinks each episode toward its series average by how much evidence it has, which is why it
          can report a margin of error (the ± SE column). It has no per-platform rows.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          In back-tests the two rank episodes similarly; M4&apos;s advantage is that its predicted gaps come true at
          about the size it predicts. M4 is the <Text strong>canonical</Text> model: its overall score is what the
          episode record carries and what the scheduler experiment draws from. V1 stays for comparison.
        </Paragraph>
      </>
    }
  />
);

export const ScoreSeHelp: React.FC = () => (
  <HelpPopover
    title="What ± SE means"
    content={
      <>
        <Paragraph>
          The score is an estimate from a limited number of airings, and SE (standard error) is its margin of error, in
          the same percent units as the score. Read <Text strong>+4.0 ±0.7</Text> as: the episode&apos;s true effect is
          most likely between +3.3 and +4.7 (about two chances in three), and almost certainly between +2.6 and +5.4
          (two SEs either side).
        </Paragraph>
        <Paragraph>
          The SE shrinks as airings accumulate, roughly with the square root of their number: four times the airings
          halves it. A new episode with two or three airings might show ±2 or more; a Wilkos episode with a dozen
          airings on Nosey INTL 1 shows about ±0.7.
        </Paragraph>
        <Paragraph>
          <Text strong>How to use it.</Text> Two scores whose ranges overlap are not really different: +4.0 ±0.7 and
          +4.3 ±1.6 are a tie, the second just has fewer airings. A score more than two SEs from zero is confidently
          above or below average. When picking episodes to act on, prefer those with a small SE; the top and bottom
          fifths used in the scheduler test are drawn from strong episodes for this reason.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          Only M4 reports it: it is the model&apos;s posterior standard deviation after shrinkage. V1 rows leave the
          column blank, because V1 blends platforms without an uncertainty of its own.
        </Paragraph>
      </>
    }
  />
);

export const SeriesHelp: React.FC = () => (
  <HelpPopover
    title="Scores are relative to the series"
    content={
      <>
        <Paragraph>
          A score says how an episode does against an <Text strong>average episode of its own series</Text>, not against
          every episode on the channel. +5% on a Sally episode means 5% above an average Sally episode; in absolute
          audience that is still less than an average Jerry Springer episode, because the series itself draws less.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          So comparing scores is most meaningful within one series, which is what this filter is for. The buckets
          (low, average, high) are cut across all series, which works because every series is centred on zero.
        </Paragraph>
      </>
    }
  />
);

/** Plain-language meanings of the columns on the Slot Strength tab. */
export const SLOT_COLUMN_HELP = {
  rawMeanRate:
    "Plain average of hours of viewing per minute of airtime over every scored airing in this slot, before " +
    "adjusting for which series aired. Proportional to the average audience watching at that hour.",
  airings:
    "Scored airings that landed in this slot over the period. Each weekday-hour comes round once a week, so a " +
    "nine-month file gives roughly 40 to 80 per slot.",
  seriesCount:
    "How many different series aired in this slot. The more series share a slot, the better its strength can be " +
    "told apart from the strength of the shows that aired there.",
  slotZ:
    "How many standard deviations this slot's strength sits from the platform's mean slot. A rescaled version of " +
    "the percent column, kept for the CSV; it no longer drives the outlier flag.",
  neighbourGap:
    "This slot's percent minus the typical percent of the two hours either side of it (the median of those four). " +
    "Near zero means the slot sits on the day's curve; a big number means it jumps away from its neighbours.",
  outlier:
    "Flagged when the slot sits 20 points or more away from the hours around it: a lone spike or dip, worth a look. " +
    "The top of the evening peak is not an outlier, however strong it is.",
  confidence:
    "Strong when at least 8 scored airings landed in the slot, thin when fewer did. A thin slot's strength can move " +
    "a lot once a few more airings arrive, so treat it as provisional.",
};

export const SlotsHelp: React.FC = () => (
  <HelpPopover
    title="What slot strength means"
    content={
      <>
        <Paragraph>
          A slot is one weekday and hour on one platform and channel, in that platform&apos;s <Text strong>local time</Text>.
          Its strength is how much audience the hour itself draws compared with an average hour on the same platform:
          +20% means an average episode airing there earns 20% more than it would in an average slot, -25% means 25% less.
        </Paragraph>
        <Paragraph>
          It is built from every series that aired in the slot, <Text strong>after taking out how strong each series is</Text>,
          so it measures the hour, not the shows. That is the number to use when moving an airing: an episode that earned 1.0
          in a -25% slot should earn about 1.33 in an average one. (The earlier Jerry and Steve analysis built its grid from
          those two series alone, so its cells read &quot;Monday with Jerry&quot; and will not match these one for one.)
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          The colour cut points are the same on every platform, so grids compare: a mostly gray grid is a flat day, a
          deeply coloured one has a strong daily rhythm. Dashed cells are thin (under 8 airings). An outlier is a slot
          that jumps 20 points or more away from the hours either side of it, not the top of the evening peak. Hover a
          cell for its airings, series count and dates; switch to Table to sort by any column.
        </Paragraph>
      </>
    }
  />
);
